import { useEffect, useRef } from 'react';

interface Options {
  /** Called when the sentinel scrolls into view. */
  onLoadMore: () => void;
  /** Skip when there's nothing more / a fetch is in flight. */
  enabled: boolean;
  /** rootMargin so we trigger before the user actually hits the bottom. */
  rootMargin?: string;
}

/**
 * Wire an IntersectionObserver on a sentinel element. Returns a ref
 * callers attach to a 1px-tall div placed at the bottom of the list.
 * Ditches its observer + re-creates one when `enabled` flips so we
 * don't fire while a fetch is already in flight.
 */
export function useInfiniteScrollSentinel({
  onLoadMore,
  enabled,
  rootMargin = '200px',
}: Options) {
  const ref = useRef<HTMLDivElement | null>(null);
  // Latest callback in a ref so the observer effect doesn't have to
  // re-subscribe every render.
  const cbRef = useRef(onLoadMore);
  useEffect(() => {
    cbRef.current = onLoadMore;
  }, [onLoadMore]);

  useEffect(() => {
    if (!enabled) return;
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            cbRef.current();
            break;
          }
        }
      },
      { rootMargin },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [enabled, rootMargin]);

  return ref;
}
